import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import {
  getBrandLogo,
  getBrandName,
  applyBrandTheme,
  updateFavicon,
  detectBrandFromInbox,
} from 'dashboard/helper/brandingHelper';

export function useBranding() {
  const store = useStore();
  const mainLogo = ref(null);
  const iconLogo = ref(null);

  /**
   * 🔥 NEW APPROACH: Inbox-based branding
   * Detects brand theme from the ACTIVE CONVERSATION's inbox
   *
   * How it works:
   * 1. Get current active conversation from Vuex store
   * 2. Extract inbox from conversation
   * 3. Use inbox.brand_theme to determine brand
   * 4. If no conversation active, return null (default Chatwoot theme)
   *
   * This allows:
   * - Single URL for all agents (chat.lhgmoteis.com.br)
   * - Each conversation shows brand colors based on inbox
   * - Easy visual identification of which brand customer is from
   */
  const brandTheme = computed(() => {
    // Get the currently selected/active conversation
    const currentConversation = store.getters.getSelectedChat;

    console.log('[BRANDING] Current conversation:', currentConversation?.id);
    console.log('[BRANDING] Conversation object:', currentConversation);
    console.log('[BRANDING] Inbox from conversation:', currentConversation?.inbox);
    console.log('[BRANDING] Inbox ID:', currentConversation?.inbox_id);

    // Try to get inbox from store if not in conversation
    const inboxId = currentConversation?.inbox_id;
    let inbox = currentConversation?.inbox;

    if (!inbox && inboxId) {
      // Inbox not populated in conversation, try to get from inboxes store
      inbox = store.getters['inboxes/getInbox'](inboxId);
      console.log('[BRANDING] Fetched inbox from store:', inbox);
    }

    // If there's an active conversation with an inbox, detect brand from it
    if (currentConversation && inbox) {
      const inboxBrand = detectBrandFromInbox(inbox);
      console.log('[BRANDING] Inbox brand:', inboxBrand);
      return inboxBrand;
    }

    // No active conversation = default Chatwoot theme
    console.log('[BRANDING] No active conversation or inbox, using default theme');
    return null;
  });

  const brandName = computed(() => {
    return getBrandName(brandTheme.value);
  });

  const brandPrimaryColor = computed(() => {
    if (!brandTheme.value) return '#2781F6';

    const colors = {
      lush: '#002E09',
      'andar-de-cima': '#101820',
      tout: '#2D0234',
      altana: '#CA98FE',
    };

    return colors[brandTheme.value] || '#2781F6';
  });

  const brandAccentColor = computed(() => {
    if (!brandTheme.value) return '#B782EF';

    const colors = {
      lush: '#B782EF',
      'andar-de-cima': '#ED0677',
      tout: '#FF0FFB',
      altana: '#002E09',
    };

    return colors[brandTheme.value] || '#B782EF';
  });

  const loadLogo = (type = 'main') => {
    try {
      const logoUrl = getBrandLogo(brandTheme.value, type);
      if (type === 'main') {
        mainLogo.value = logoUrl;
      } else {
        iconLogo.value = logoUrl;
      }
      return logoUrl;
    } catch (error) {
      return null;
    }
  };

  watch(
    brandTheme,
    newTheme => {
      applyBrandTheme(newTheme);
      updateFavicon(newTheme);
      loadLogo('main');
      loadLogo('icon');
    },
    { immediate: true }
  );

  const isBrand = brand => {
    return brandTheme.value === brand;
  };

  const hasCustomBranding = computed(() => {
    return brandTheme.value !== null;
  });

  /**
   * Helper function to get brand theme for a specific inbox
   * Useful for displaying brand indicators in conversation lists
   * @param {Object} inbox - Inbox object
   * @returns {string|null} - Brand theme name
   */
  const getBrandThemeForInbox = inbox => {
    return detectBrandFromInbox(inbox);
  };

  return {
    brandTheme,
    brandName,
    brandPrimaryColor,
    brandAccentColor,
    mainLogo,
    iconLogo,
    hasCustomBranding,
    loadLogo,
    isBrand,
    getBrandThemeForInbox, // NEW: Helper for inbox-specific branding
  };
}

export default useBranding;
